import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Modal, Form, Badge, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { adminApi } from '../api/api';
import { useAuth } from '../hooks/useAuth';

export default function Admin() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showReset, setShowReset] = useState(null);
  const [form, setForm] = useState({ username: '', email: '', password: '', role: 'user' });
  const [newPassword, setNewPassword] = useState('');
  const [systemBot, setSystemBot] = useState(null);

  const fetchUsers = () => adminApi.listUsers().then((r) => setUsers(r.data)).catch(() => {});
  const fetchSystemBot = () => adminApi.systemBot().then((r) => setSystemBot(r.data)).catch(() => {});

  useEffect(() => {
    fetchUsers();
    fetchSystemBot();
    if (new URLSearchParams(window.location.search).get('twitch_bot') === 'connected') {
      toast.success('Zentraler Bot verbunden');
    }
  }, []);

  const handleDisconnectSystemBot = async () => {
    if (!window.confirm('Zentralen Bot wirklich trennen? Streamer ohne eigenen Bot schreiben dann über ihr Haupt-Konto.')) return;
    await adminApi.disconnectSystemBot();
    toast.info('Zentraler Bot getrennt');
    fetchSystemBot();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await adminApi.createUser(form);
      toast.success(`User '${form.username}' erstellt`);
      setShowCreate(false);
      setForm({ username: '', email: '', password: '', role: 'user' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const handleToggleActive = async (u) => {
    await adminApi.updateUser(u.id, { is_active: !u.is_active });
    fetchUsers();
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`User '${u.username}' wirklich löschen?`)) return;
    await adminApi.deleteUser(u.id);
    toast.info(`User '${u.username}' gelöscht`);
    fetchUsers();
  };

  const handleResetPassword = async () => {
    try {
      await adminApi.resetPassword(showReset.id, newPassword);
      toast.success('Passwort geändert');
      setShowReset(null);
      setNewPassword('');
    } catch {
      toast.error('Fehler');
    }
  };

  return (
    <div>
      <Card bg="dark" text="white" className="border-secondary mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Card.Title className="mb-0">🤖 Zentraler Bot</Card.Title>
            <Badge bg={systemBot?.connected && systemBot.scopes_ok && !systemBot.needs_reauth ? 'success' : 'secondary'}>
              {systemBot?.connected ? 'Verbunden' : 'Nicht verbunden'}
            </Badge>
          </div>
          <p className="text-muted small">
            Dieser Twitch-Account schreibt standardmäßig für alle Streamer im Chat. Streamer können in ihren
            Twitch-Einstellungen optional einen eigenen Bot-Account verwenden.
          </p>
          {systemBot?.connected ? (
            <>
              <p className="mb-2">
                Konto: <strong>{systemBot.display_name}</strong>
                <span className="text-muted ms-2">({systemBot.channel_name})</span>
              </p>
              {(!systemBot.scopes_ok || systemBot.needs_reauth) && (
                <Alert variant="warning" className="py-2">
                  Der Bot muss neu verbunden werden (fehlende oder widerrufene Berechtigungen).
                </Alert>
              )}
              <Button variant="outline-primary" size="sm" className="me-2" onClick={() => adminApi.connectSystemBot()}>
                Neu verbinden
              </Button>
              <Button variant="outline-danger" size="sm" onClick={handleDisconnectSystemBot}>Trennen</Button>
            </>
          ) : (
            <>
              <p className="text-muted small">
                Melde dich in einem separaten Browserfenster (oder Inkognito) beim Bot-Account an, bevor du verbindest.
              </p>
              <Button variant="outline-primary" size="sm" onClick={() => adminApi.connectSystemBot()}>
                🟣 Zentralen Bot verbinden
              </Button>
            </>
          )}
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="text-white mb-0">⚙️ Benutzerverwaltung</h4>
        <Button variant="success" onClick={() => setShowCreate(true)}>+ Neuer User</Button>
      </div>

      <Card bg="dark" text="white" className="border-secondary">
        <Card.Body>
          <Table variant="dark" striped hover responsive>
            <thead>
              <tr>
                <th>Benutzername</th>
                <th>E-Mail</th>
                <th>Rolle</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.username}
                    {u.id === currentUser?.id && (
                      <Badge bg="info" className="ms-2">Ich</Badge>
                    )}
                  </td>
                  <td>{u.email}</td>
                  <td>
                    <Badge bg={u.role === 'admin' ? 'danger' : 'secondary'}>
                      {u.role}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg={u.is_active ? 'success' : 'dark'} className="border border-secondary">
                      {u.is_active ? 'Aktiv' : 'Deaktiviert'}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex gap-1 flex-wrap">
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => setShowReset(u)}
                      >
                        PW
                      </Button>
                      {u.id !== currentUser?.id && (
                        <>
                          <Button
                            variant={u.is_active ? 'outline-secondary' : 'outline-success'}
                            size="sm"
                            onClick={() => handleToggleActive(u)}
                          >
                            {u.is_active ? 'Sperren' : 'Aktivieren'}
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(u)}
                          >
                            ✕
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Create User Modal */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} data-bs-theme="dark">
        <Modal.Header closeButton>
          <Modal.Title>Neuen User erstellen</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreate}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Benutzername</Form.Label>
              <Form.Control required value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>E-Mail</Form.Label>
              <Form.Control type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Passwort</Form.Label>
              <Form.Control type="password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Rolle</Form.Label>
              <Form.Select value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="user">user</option>
                <option value="admin">admin</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Abbrechen</Button>
            <Button type="submit" variant="success">Erstellen</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal show={!!showReset} onHide={() => setShowReset(null)} data-bs-theme="dark">
        <Modal.Header closeButton>
          <Modal.Title>Passwort für '{showReset?.username}' ändern</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            type="password"
            placeholder="Neues Passwort"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReset(null)}>Abbrechen</Button>
          <Button variant="warning" onClick={handleResetPassword}>Speichern</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
